import React, { useState } from 'react';
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Typography,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';

import { PrimaryButton, SecondaryButton } from 'src/components/ui/Buttons';
import { borderColor, primaryGreen } from 'src/data/colors';
import { savePoints } from 'src/state/entities/points/actions';
import { savePointsLoading } from 'src/state/entities/points/selectors';
import PointsSearch from 'src/components/ui/pointsUI/PointsSearch';

import { PointsProps } from './types/types';
import ReadButton from './ReadButton';
import { WidgetSections } from './types/pointsWidgetstypes';
import WidgetRenderer from '../../ui/pointsUI/widgets/WidgetRenderer';
import { usePointsSearch } from './hooks/usePointsSearch';
import { getPointId } from './helpers';
import { getSiteContext, getSiteLastQueryTime } from 'src/state/contexts/site';
import StatusIndicator from 'src/components/ui/statusIndicator';

interface TabContentProps {
  sectionsWidgets: WidgetSections;
  title: string;
  time: string;
  readPoints: () => void;
  siteId: number;
}

type InputValues = {
  modbusPoints: Array<{
    address: string;
    value: number;
  }>;
  sunspecPoints: Array<{
    model: number;
    block: string;
    point: string;
    value: number;
  }>;
};

const useStyles = makeStyles((theme) => ({
  button: {
    backgroundColor: 'white',
    border: `1px solid ${primaryGreen}`,
    borderRadius: 8,
    color: primaryGreen,
    fontSize: 16,
    fontWeight: 500,
    height: 40,
    margin: '0 0 0 5px',
    padding: '8px 16px',
    '&:hover': {
      background: primaryGreen,
    },
  },
  saveButton: {
    paddingRight: 8,
    [theme.breakpoints.up('sm')]: {
      paddingRight: 0,
    },
  },
  container: {
    border: `1px solid ${borderColor}`,
    borderRadius: 8,
    padding: 16,
  },
  description: {
    fontSize: 16,
  },
  time: {
    fontSize: 12,
    opacity: 0.5,
    lineHeight: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: 500,
  },
  inputSection: {
    marginTop: 16,
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(345px, 1fr))',
    gap: 8,
    [theme.breakpoints.down('xs')]: {
      gridTemplateColumns: 'repeat(auto-fill, minmax(100%, 1fr))',
    },
  },
  searchField: {
    marginBottom: 16,
    width: '100%',
  },
  mobileSpacing: {
    marginTop: 8,
    [theme.breakpoints.up('sm')]: {
      marginTop: 0,
    },
  },
  mobileFullWidth: {
    '& > *': {
      width: '100%',
      [theme.breakpoints.up('sm')]: {
        width: 'auto',
      },
    },
  },
}));

const formatTime = (timeString: string) =>
  timeString ? format(new Date(timeString), 'MMM d, yyyy, h:mm:ss a zzz') : timeString;

const TabContent: React.FC<TabContentProps> = ({ time, readPoints, siteId, sectionsWidgets }) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const [lastWriteTime, setLastWriteTime] = useState<Date>();
  const isSaving = useSelector(savePointsLoading);
  const { searchTerm, searchResults, handleSearch, clearSearch } = usePointsSearch(sectionsWidgets);
  const site = useSelector(getSiteContext);
  const siteFetchTime = useSelector(getSiteLastQueryTime);
  const [inputValues, setInputValues] = useState<Record<string, string | number | string[]>>(
    Object.values(sectionsWidgets).reduce((acc, { widgets }) => {
      return widgets.reduce((widgetsAcc, { points }) => {
        const pointValues = points.reduce((pointsAcc, point) => {
          const id = getPointId(point);
          const {
            entry: { dType },
            scaledValue,
            value,
          } = point;
          return {
            ...pointsAcc,
            [id]: dType === 'bitfield' || dType === 'bitfield16' ? value : scaledValue,
          };
        }, {});
        return { ...acc, ...widgetsAcc, ...pointValues };
      }, acc);
    }, {} as Record<string, string | number | string[]>)
  );

  const [updatedInputValues, setUpdatedInputValues] = useState<InputValues>({
    modbusPoints: [] as InputValues['modbusPoints'],
    sunspecPoints: [] as InputValues['sunspecPoints'],
  });
  const [sectionOpen, setSectionOpen] = useState<string | null>(null);

  const formIsDirty =
    Object.keys(updatedInputValues.modbusPoints).length +
      Object.keys(updatedInputValues.sunspecPoints).length >
    0;

  const classes = useStyles();

  const handleChangeInput = (point: PointsProps, value: string | number | string[]) => {
    const scaleFactor =
      Array.isArray(point.value) || point.scaledValue === 0 ? 1 : point.value / point.scaledValue;
    const scaledValue = Number(value) * scaleFactor;

    if (point.protocol === 'modbus') {
      setUpdatedInputValues((prevInputValues) => ({
        ...prevInputValues,
        modbusPoints: [
          ...prevInputValues.modbusPoints.filter((p) => p.address !== point.address),
          { address: point.address, value: scaledValue } as InputValues['modbusPoints'][number],
        ],
      }));
    } else {
      setUpdatedInputValues((prevInputValues) => ({
        ...prevInputValues,
        sunspecPoints: [
          ...prevInputValues.sunspecPoints.filter((p) => p.model !== point.modelId),
          {
            model: point.modelId,
            block: point.block,
            point: point.point,
            value: scaledValue,
          } as InputValues['sunspecPoints'][number],
        ],
      }));
    }

    setInputValues((prevInputValues) => ({
      ...prevInputValues,
      [getPointId(point)]: value,
    }));
  };

  const onSave = async () => {
    if (formIsDirty) {
      dispatch(savePoints.request(siteId, updatedInputValues));
      setLastWriteTime(new Date());
      setUpdatedInputValues({
        modbusPoints: [],
        sunspecPoints: [],
      });
    }
  };

  return (
    <Grid container justifyContent="center">
      <Grid container item xs={12} spacing={2}>
        <Grid container item xs={12} justifyContent="space-between">
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center">
              <Box display="flex" alignItems="center">
                <StatusIndicator
                  isOnline={site.isOnline}
                  siteFetchTime={siteFetchTime ? new Date(siteFetchTime) : undefined}
                />
              </Box>
              <Box ml={1} display="flex" alignItems="center">
                <Typography className={classes.time}>Last updated: {formatTime(time)}</Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={8} container justifyContent="flex-end" spacing={isMobile ? 0 : 2}>
            <Grid item xs={12} sm="auto" className={classes.mobileSpacing}>
              <PointsSearch searchTerm={searchTerm} onSearch={handleSearch} onClear={clearSearch} />
            </Grid>
            <Grid
              item
              xs={8}
              sm="auto"
              className={`${classes.mobileFullWidth} ${classes.mobileSpacing} ${classes.saveButton}`}
            >
              <PrimaryButton onClick={onSave} disabled={isSaving || !formIsDirty}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </PrimaryButton>
            </Grid>
            <Grid
              item
              xs={4}
              sm="auto"
              className={`${classes.mobileFullWidth} ${classes.mobileSpacing}`}
            >
              <ReadButton readPoints={readPoints} lastWriteTime={lastWriteTime} />
            </Grid>
          </Grid>
        </Grid>

        {/* search results */}
        {!!searchTerm && (
          <Grid item xs={12}>
            <div className={classes.container}>
              <Typography className={classes.title}>Search Results</Typography>
              <div className={classes.inputSection}>
                {searchResults && searchResults.length > 0 ? (
                  searchResults.map((widget, index) => (
                    <WidgetRenderer
                      key={index}
                      widget={widget}
                      inputValues={inputValues}
                      onChange={handleChangeInput}
                    />
                  ))
                ) : (
                  <Typography>No results found</Typography>
                )}
              </div>
            </div>
          </Grid>
        )}

        {!searchTerm &&
          Object.entries(sectionsWidgets).map(([sectionKey, section], sectionIndex) => (
            <Grid item key={sectionKey} xs={12}>
              <div className={classes.container}>
                <Grid
                  container
                  item
                  xs={12}
                  justifyContent="space-between"
                  alignItems="center"
                  onClick={() => setSectionOpen(sectionOpen === sectionKey ? null : sectionKey)}
                >
                  <Grid item xs={10} sm="auto">
                    <Typography className={classes.title}>{sectionKey}</Typography>
                  </Grid>
                  <Grid item xs={2} sm="auto">
                    {isMobile ? (
                      <IconButton>
                        {sectionOpen === sectionKey ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    ) : (
                      <SecondaryButton>
                        {sectionOpen === sectionKey ? 'Collapse' : 'Expand'}
                      </SecondaryButton>
                    )}
                  </Grid>
                </Grid>
                <Collapse in={sectionOpen === sectionKey}>
                  <div className={classes.inputSection}>
                    {!isEmpty(inputValues) &&
                      section.widgets.map((widget, index) => (
                        <WidgetRenderer
                          key={widget.widget + index}
                          widget={widget}
                          inputValues={inputValues}
                          onChange={handleChangeInput}
                        />
                      ))}
                  </div>
                </Collapse>
              </div>
            </Grid>
          ))}
      </Grid>
    </Grid>
  );
};

export default TabContent;
